import React from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { EditableText, PanelHeaderButton } from '~/design-system/components/v1_5/Layout';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
/*
 * TWO stylesheets, deliberately. `ImageCard.module.css` owns everything an artifact card shares with
 * a photo card — the cell, the card's selection ring and focus, the `⋯`, and the entire drag /
 * overlay / landing sequence, which took four passes to get right and must stay identical for both.
 * This file's own stylesheet carries only what differs: the slide's ratio and the editing caption.
 */
import shell from './ImageCard.module.css';
import styles from './ArtifactCard.module.css';
import type { DirectoryItem, DirectoryItemDrag, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ArtifactCardProps {
  item: DirectoryItem;
  selected?: boolean;
  onClick?: () => void;
  /** Omit and the card offers no `⋯` at all — which is what the drag overlay's copy does. */
  onMoreClick?: () => void;
  menuOpen?: boolean;
  /** The name is open for editing. Controlled by the panel, exactly as a row's name is. */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /** Fires only for a name that actually changed. Omit it and the card never opens for editing. */
  onRename?: (next: string) => void;
  theme?: HeaderTheme;
  /** Stories and visual tests only: paints the hover state without a real pointer. */
  forceHover?: boolean;
  /** Drag-to-reorder wiring from `SortableDirectoryItem`. */
  drag?: DirectoryItemDrag;
  /** This is the lifted copy in the drag overlay rather than a card in the grid. */
  overlay?: boolean;
  /** What plays over the thumbnail while the artifact is being written: the host's morph, scaled. */
  working?: React.ReactNode;
  className?: string;
}

/**
 * One artifact, as its own render — the slide rail every presentation tool draws.
 *
 * Artifacts used to be rows: a 34x19 chip of the slide, its name, and `artifact 01 · 2h ago`. The
 * chip was too small to read and the name was doing the identifying, which is backwards — the whole
 * reason a deck is a visual medium is that you know a slide by looking at it. So the render is the
 * item now, at the full width of the pane. The two strings stay under it (Guri, 2026-09-02: "a title
 * on each of these tiles, and the date when it's updated"), in the photo card's own two classes, so
 * the rail and the row list say exactly the same name and the same timestamp.
 *
 * **One column, where images take two.** A photo in a 2-up grid is still legible because a photo is
 * a picture of one thing; a slide is a page of TEXT, and at half the pane's width its headline is
 * four pixels tall and the card identifies nothing. One column is the smallest grid a slide can be
 * read in, and it is what Figma Slides, Keynote and Google Slides all settle on for the same reason.
 *
 * Every other behaviour is `ImageCard`'s, down to the stylesheet: click selects, double-click
 * renames, the `⋯` opens the row menu, and the drag is the same lift and the same landing.
 */
export function ArtifactCard({
  item,
  selected = false,
  onClick,
  onMoreClick,
  menuOpen = false,
  editing = false,
  onEditingChange,
  onRename,
  theme: themeProp,
  forceHover = false,
  drag,
  overlay = false,
  working,
  className,
}: ArtifactCardProps) {
  const theme = useV15Theme(themeProp);
  const status = item.status ?? 'ready';
  const renameable = onRename !== undefined && status === 'ready';
  const hasMenu = onMoreClick !== undefined && status === 'ready' && !editing;
  /**
   * WAITING IS NOT WORKING, and the card had no way to say either.
   *
   * A queued artifact has not been written yet — there is no document, so there is no render, and
   * there never was one to lose. The card drew that as an empty plate, which is the SAME plate a
   * finished artifact shows while its thumbnail is still rastering and the same one it shows if the
   * raster failed. Three different states, one grey box, and the words underneath were the only clue
   * (Beka, 2026-09-10, twice, with screenshots of exactly this).
   *
   * `DirectoryRow` has always drawn the distinction — `STATUS_GLYPH.queued` is a clock, with the note
   * that nothing is happening to this item yet so nothing about its mark should move. That whole
   * treatment was dropped when artifacts became cards. This is it, put back: the row's own glyph, on
   * the card's plate.
   *
   * `generating` deliberately does NOT come through here. It already has a better answer — the host
   * mounts the preview's own morph in the `working` slot below, which is motion, and motion is
   * exactly the thing a queued item must not have.
   */
  const waiting = status === 'queued';
  const body = (
    <>
      <span
        className={cn(shell.thumb, styles.thumb)}
        style={item.thumbnail ? ({ '--hv15-dir-thumb': item.thumbnail } as React.CSSProperties) : undefined}
        aria-hidden
      >
        {/* An artifact being written: the host mounts the SAME morph the preview plays, scaled to
            the thumbnail (Guri, 2026-09-08: "reuse that same morph effect here in the directory
            panel"). The card only offers the slot; it draws no loader of its own. */}
        {working ? <span className={styles.working}>{working}</span> : null}
        {waiting && !working ? (
          <span className={styles.waiting}>
            <DeckIcon name="clock" size={18} />
          </span>
        ) : null}
      </span>
      {/* The name and the timestamp line, at rest and while editing — `ImageCard`'s `.name` and
          `.dims`, so the two cards caption identically and selection promotes the name the same
          way. The field opens in place, one line, exactly where the name already is. */}
      <EditableText
        className={shell.name}
        value={item.title}
        editing={editing}
        onEditingChange={(next) => onEditingChange?.(next)}
        onCommit={(next) => onRename?.(next)}
        label={`Rename ${item.title}`}
      />
      <span className={shell.dims}>{item.meta}</span>
    </>
  );

  return (
    <div
      ref={drag?.ref}
      className={cn(
        themes[theme],
        shell.cell,
        forceHover && shell.forceHover,
        drag?.dragging && shell.dragging,
        overlay && shell.overlay,
        className,
      )}
      style={drag?.style}
      // The panel finds this cell to place the row menu against it, exactly as it finds a row.
      data-directory-row={item.id}
    >
      {editing ? (
        /* Not a button while the name is a field — see `DirectoryRow`'s `Row` for the rule. */
        <div className={cn(shell.card, selected && shell.selected)}>{body}</div>
      ) : (
        <button
          type="button"
          ref={drag?.handleRef}
          {...drag?.handleProps}
          className={cn(shell.card, selected && shell.selected, forceHover && shell.forceHover)}
          aria-current={selected ? 'true' : undefined}
          // One accessible name for the whole card, the two lines joined, so a reader hears the
          // slide and its date as one item rather than a button and two stray strings.
          aria-label={`${item.title} — ${item.meta}`}
          title={item.title}
          onClick={onClick}
          // Two clicks land before this does, and selecting the same id twice is the same state —
          // the only reason the gesture can sit on top of a click-to-select card at all.
          onDoubleClick={renameable ? () => onEditingChange?.(true) : undefined}
        >
          {body}
        </button>
      )}
      {hasMenu ? (
        /* A sibling of the card button — a button inside a button is invalid markup and swallows
           the outer click. It floats over the render's corner, so it sits on an overlay plate: a
           bare glyph over an arbitrary slide is illegible half the time. `ImageCard`'s own, prop
           for prop. */
        <PanelHeaderButton
          icon="more"
          activeIcon="more-fill"
          active={menuOpen}
          hover="tone"
          label={`More actions for ${item.title}`}
          ariaHasPopup="menu"
          ariaExpanded={menuOpen}
          theme={theme}
          className={cn(shell.more, styles.more, menuOpen && shell.moreOpen)}
          onClick={onMoreClick}
        />
      ) : null}
    </div>
  );
}
